import React, {
  useContext,
} from 'react';
import {
  VisitNoteServiceCode,
} from 'views/EMR/components/ServiceLookup/type';
import ToolTip from 'core/components/Tooltip';
import {
  isEmptyString,
} from '@populateui/utils';
import {
  VirtualTableColumnDef as ColumnDef,
  EditableTableMeta,
} from 'core/components/VirtualTable/types';
import DragNDropIcon from 'core/icons/dragNDrop';
import {
  CellContext,
} from '@tanstack/react-table';
import {
  ServiceContext,
} from 'views/EMR/components/ServiceLookup/ServiceContext';
import Chip from 'core/components/Chip';
import clsx from 'clsx';

type Part = 'root' | 'chipWrapper';

type CodeWithDescriptionProps = {
  value: string;
  description?: string;
  onRemove?: () => void;
  readonly?: boolean;
  className?: string;
  classes?: Partial<Record<Part, string>>;
};

export function CodeWithDescription({
  value,
  description,
  onRemove,
  readonly,
  className,
  classes,
}: CodeWithDescriptionProps) {
  return (
    <div
      className={clsx(
        'flex gap-2 items-center',
        classes?.root,
        { 'cursor-grab': !readonly },
      )}
    >
      {!readonly && <DragNDropIcon className="!text-gray-400 !h-4 !w-4" />}
      <ToolTip
        title={(
          <div className="max-w-[600px] overflow-auto">
            {description}
          </div>
        )}
        hide={isEmptyString(description)}
        placement="top-start"
        classes={{ tooltip: 'max-h-[300px] overflow-auto' }}
        isInteractive
      >
        <div className={clsx('overflow-hidden', classes?.chipWrapper)}>
          <Chip
            value={value}
            onClose={onRemove}
            disabled={readonly}
            className={className}
          />
        </div>
      </ToolTip>
    </div>
  );
}

function ServiceNameCell({ row, table }: CellContext<VisitNoteServiceCode, unknown>) {
  const { removeData } = table.options.meta as EditableTableMeta<VisitNoteServiceCode>;
  const { description, serviceName } = row.original ?? {};
  const { readonly } = useContext(ServiceContext);

  return (
    <CodeWithDescription
      value={serviceName ?? ''}
      description={description ?? ''}
      readonly={readonly}
      classes={{ root: '!w-full', chipWrapper: 'flex-1' }}
      onRemove={() => { removeData?.(row.index, row.original); }}
    />
  );
}

export default {
  id: 'serviceName',
  accessorKey: 'serviceName',
  header: 'Service',
  size: 220,
  cell: ServiceNameCell,
} as ColumnDef<VisitNoteServiceCode>;
